import React, { useState, useEffect, useCallback, useMemo, memo } from 'react'
import { FilterMenu } from './components/FilterMenu'
import queryString from 'query-string'
import { Searchbar } from './components/Searchbar'
import debounce from 'lodash.debounce'
import { TabBar } from './components/TabBar'
import { ArticleList } from './components/results/ArticleList'
import { ImageGrid } from './components/results/ImageGrid'
import { MediaGrid } from './components/results/MediaGrid'
import { ResultsHeader } from './components/ResultsHeader'
import { TotalResultsText } from './components/TotalResultsText'
import { SortBy } from './components/SortBy'
import { Translate, TranslationsContext, useTranslations } from './TranslationsContext'
import { TrainingGrid } from './components/results/TrainingGrid'

const queryStringOptions = { arrayFormat: 'bracket' } as const

const fetchPage = async (
  endpoint: string,
  page: number,
  args: {
    itemsPerPage?: number
    query?: string
    filters?: Array<{ key: string; items: number[] }>
    sortOption?: string
  } = {}
) => {
  const params = {
    page,
    itemsPerPage: args.itemsPerPage ?? 5,
    query: args.query ?? '',
    filters: args.filters ?? [],
    sorting: args.sortOption ?? ''
  }

  const originalQueryParams = queryString.parse(window.location.search, queryStringOptions)

  if (originalQueryParams != null) {
    for (const property in originalQueryParams) {
      if (property.startsWith('filter') && params.filters.length == 0) {
        console.log(property);
        let key = property.replace('filter_', '');
        let numbersAsStringArray = (originalQueryParams[property] || []) as string[];
        let numbers = numbersAsStringArray.map((i) => Number(i));
        var element = { key: key, items: numbers };
        console.log(numbers);
        console.log(key);
        console.log(element);
        params.filters.push(element);
      }
      console.log("kongen");
    }
  }


  const filterProperties =
    args.filters
      ?.filter((f) => f.key !== 'type')
      .reduce((acc, f) => {
        const key = `filter_${f.key}`
        return {
          ...acc,
          [key]: f.items
        }
      }, {}) ?? []

  const newQuery = queryString.stringify(
    {
      ...originalQueryParams,
      // filters: args.filters?.filter((f) => f.key !== 'type').flatMap((f) => f.items) ?? [],
      type: args.filters?.find((f) => f.key === 'type')?.items ?? [],
      ...(args.query != null && args.query.length > 0 ? { query: args.query } : {}),
      ...(args.sortOption != null && args.sortOption.length > 0 ? { sort: args.sortOption } : {}),
      ...filterProperties
    },
    queryStringOptions
  )

  window.history.replaceState(
    undefined,
    '',
    window.location.protocol +
    '//' +
    window.location.host +
    window.location.pathname +
    window.location.hash.split('?')[0] +
    '?' +
    newQuery
  )

  const response = await fetch(endpoint, {
    method: 'POST',
    body: JSON.stringify(params)
  })

  return response.json()
}

export type ArticleFromServer = {
  date: string
  title: string
  intro: string | null
  link: string
  loginRequired: boolean
  additionalInfo: string
  newWindow: boolean | undefined
}

export type URPlusProductResultFromServer = {
  companyName: string
  intro: string
  isApplicationKit: boolean
  isFeatured: boolean
  isSolution: boolean
  link: string
  title: string
  image: string
}

export type URTrainingResultFromServer = {
  availableSeats: number
  availableSeatsText: string
  dateText: string
  endDate: string
  id: number
  image: string
  languages: string
  locationText: string
  name: string
  startDate: string
  totalSeats: number
  typeColor: string
  typeText: string
  url: string
}

export type MediaResultFromServer = {
  id: string
  title: string
  extension: ReadonlyArray<string>
  description: string
  fileSize: number
  type: string
  thumbnail: string
}

export type ResultGroupFromServer = {
  title: string
  results: Array<ArticleFromServer | URPlusProductResultFromServer | MediaResultFromServer | URTrainingResultFromServer>
  total: number
  filter: { key: string; value: number }
}

export type FilterFromServer = {
  id?: string // Sometimes id is unfortunately not supplied
  name: string
  category?: string
  options: Array<{ id: number; name: string; count?: number }>
}

type AppProps = {
  endpoint: string
  resultsPerPage: number
  initialResults: number
  hideFilter: boolean
  hideResultsHeader: boolean
  initialTypeFilter: number | undefined
}

export const App: React.FC<AppProps> = ({
  endpoint,
  resultsPerPage,
  initialResults,
  hideFilter,
  hideResultsHeader,
  initialTypeFilter
}) => {
  const parsedQS = queryString.parse(window.location.search, queryStringOptions)
  const [translations, setTranslations] = useState<Record<string, string>>({})
  const [selectedSortOption, setSelectedSortOption] = useState<string | undefined>(
    parsedQS.sort == null ? undefined : (parsedQS.sort as string)
  )
  const [sortingOptions, setSortingOptions] = useState<Array<{ label: string; value: string }>>([])
  const [resultViewType, setResultViewType] = useState<string | undefined>('loading')
  const [isLoading, setLoading] = useState(true)
  const [isInlineLoading, setInlineLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [currentPageIndex, setCurrentPageIndex] = useState(0)
  const [results, setResults] = useState<Array<ResultGroupFromServer>>([])
  const [initialized, setInitialized] = useState(false)
  const [typeFilter, setTypeFilter] = useState<FilterFromServer>({ name: 'type', options: [] })
  const [filters, setFilters] = useState<Array<FilterFromServer>>([])
  const [activeFilters, setActiveFilters] = useState<number[]>(
    ((parsedQS.filters as string[]) ?? []).map((x) => parseInt(x, 10))
  )
  const [activeTypeFilter, setActiveTypeFilter] = useState<number | undefined>(
    Object.keys(parsedQS).length === 0
      ? initialTypeFilter
      : parsedQS.type == null
        ? undefined
        : parseInt(parsedQS.type as string)
  )
  const [query, setQuery] = useState<string>((parsedQS.query as string) ?? '')
  const [disableSearchBar, setDisableSearchBar] = useState(false)
  const [contentBelowFilters, setContentBelowFilters] = useState<string | undefined>(undefined)

  const totalResults = results.reduce((acc, cur) => acc + cur.total, 0)

  const filtersToApply = useMemo(
    () =>
      Object.entries(
        activeFilters.reduce<Record<string, number[]>>(
          (acc, cur) => {
            const group = filters.find((f) => f.options.find((o) => o.id === cur) !== undefined)
            const groupName = group?.id ?? group?.name ?? `type${cur}`
            const currentFiltersForGroup = (Reflect.get(acc, groupName) ?? []) as number[]

            return {
              ...acc,
              [groupName]: [...currentFiltersForGroup, cur]
            }
          },
          {}
          //activeTypeFilter === undefined ? {} : { type: [activeTypeFilter] }
        )
      ).map(([key, items]) => ({ key, items })),
    [activeFilters, filters]
  )

  const fetchFirstPage = useCallback(
    debounce(
      (
        _filters: typeof filters,
        _activeFilters: typeof activeFilters,
        _query: typeof query,
        _filtersToApply: typeof filtersToApply,
        _activeTypeFilter: typeof activeTypeFilter,
        _sortOption: typeof selectedSortOption
      ) => {
        setLoading(true)
        setResults([])
        fetchPage(endpoint, 0, {
          itemsPerPage: initialResults,
          query: _query,
          filters: [
            ..._filtersToApply,
            ...(_activeTypeFilter === undefined
              ? []
              : [
                {
                  key: 'type',
                  items: [_activeTypeFilter]
                }
              ])
          ],
          sortOption: _sortOption
        })
          .then((page) => {
            setLoading(false)
            setCurrentPageIndex(1)
            setResults(page.resultGroups)
            setFilters(page.filters)
            setTypeFilter({ name: 'type', options: page.types ?? [] })
            setInitialized(true)
            setDisableSearchBar(page.disableSearchBar ?? false)
            setResultViewType(page.resultViewType)
            setSelectedSortOption(page.defaultSortOption)
            setSortingOptions(page.sortOptions?.map((x: any) => ({ label: x.name, value: x.id })) ?? [])
            setTranslations(page.translations)
            setContentBelowFilters(page.contentBelowFilters)
          })
          .catch((e) => {
            // eslint-disable-next-line no-console
            console.log('error', e)
            setHasError(true)
          })
      },
      1000
    ),
    []
  )

  const applyFilters = useCallback(
    debounce(
      (
        _filters: typeof filters,
        _activeFilters: typeof activeFilters,
        _query: typeof query,
        _filtersToApply: typeof filtersToApply,
        _activeTypeFilter: typeof activeTypeFilter,
        _sortOption: typeof selectedSortOption
      ) => {
        setLoading(true)
        setResults([])
        fetchPage(endpoint, 0, {
          itemsPerPage: initialResults,
          query: _query,
          filters: [
            ..._filtersToApply,
            ...(_activeTypeFilter === undefined
              ? []
              : [
                {
                  key: 'type',
                  items: [_activeTypeFilter]
                }
              ])
          ],
          sortOption: _sortOption
        })
          .then((page) => {
            setLoading(false)
            setCurrentPageIndex(1)
            setResults(page.resultGroups)
            setFilters(page.filters)
            setInitialized(true)
          })
          .catch((e) => {
            // eslint-disable-next-line no-console
            console.log('error', e)
            setHasError(true)
          })
      },
      1000
    ),
    []
  )

  const fetchNextPage = useCallback(() => {
    setInlineLoading(true)
    setCurrentPageIndex(currentPageIndex + 1)
    fetchPage(endpoint, 0, {
      itemsPerPage: initialResults + (currentPageIndex + 1) * resultsPerPage,
      query: query,
      filters: [
        ...filtersToApply,
        ...(activeTypeFilter === undefined
          ? []
          : [
            {
              key: 'type',
              items: [activeTypeFilter]
            }
          ])
      ],
      sortOption: selectedSortOption
    })
      .then((page) => {
        setResults(page.resultGroups)
        setInlineLoading(false)
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.log('error', e)
        setHasError(true)
      })
  }, [
    selectedSortOption,
    currentPageIndex,
    endpoint,
    initialResults,
    resultsPerPage,
    query,
    filtersToApply,
    activeTypeFilter
  ])

  useEffect(
    () => {
      if (!initialized) {
        fetchFirstPage(filters, activeFilters, query, filtersToApply, activeTypeFilter, selectedSortOption)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  useEffect(
    () => {
      if (initialized) {
        applyFilters(filters, activeFilters, query, filtersToApply, activeTypeFilter, selectedSortOption)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [applyFilters, activeFilters, query]
  )

  const clearAll = useCallback(() => {
    setActiveFilters([])
  }, [setActiveFilters])

  const onSeeMore = useCallback(
    (filterId: number) => {
      setActiveTypeFilter(filterId)
      applyFilters(filters, activeFilters, query, filtersToApply, filterId, selectedSortOption)
    },
    [activeFilters, applyFilters, filters, filtersToApply, query, selectedSortOption]
  )

  const { searchInputPlaceholder } = useTranslations()

  let apiHost = ''
  try {
    apiHost = new URL(endpoint).origin
  } catch (error) { }

  // wait for the first web request, st. we may render the filter menu without it jumping
  if (initialized === false) {
    return <></>
  }

  const sortBy = selectedSortOption !== undefined && (
    <SortBy
      selectedItem={selectedSortOption}
      options={sortingOptions}
      onChange={(sortOption) => {
        setSelectedSortOption(sortOption)
        //setLoading(true)
        applyFilters(filters, activeFilters, query, filtersToApply, activeTypeFilter, sortOption)
      }}
    />
  )

  return (
    <TranslationsContext.Provider value={translations}>
      <div className="grid-outer-container grid-gap2-above">
        {disableSearchBar !== true && (
          <div className="grid-row">
            <div className="grid-span12 grid-gap1-below">
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  setLoading(true)
                  applyFilters(filters, activeFilters, query, filtersToApply, activeTypeFilter, selectedSortOption)
                  return false
                }}
              >
                <Searchbar
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={searchInputPlaceholder}
                />
              </form>
            </div>
          </div>
        )}
        <div className="grid-row">
          {hideFilter === false && (
            <div className="grid-span3 grid-tablet-hidden" style={{ paddingRight: '18px' }}>
              <FilterMenu
                filters={filters}
                activeFilters={activeFilters}
                setActiveFilters={setActiveFilters}
                clearAll={clearAll}
                contentBelowFilters={contentBelowFilters}
              />
            </div>
          )}
          <div className={hideFilter === false ? 'grid-span9 grid-tablet-span12' : 'grid-span12'}>
            {hideFilter === false && (
              <div className="grid-hidden grid-tablet-visible grid-gap3-below">
                <FilterMenu
                  filters={filters}
                  activeFilters={activeFilters}
                  setActiveFilters={setActiveFilters}
                  clearAll={clearAll}
                  contentBelowFilters={contentBelowFilters}
                />
              </div>
            )}

            <Content
              apiHost={apiHost}
              hasError={hasError}
              isLoading={isLoading}
              isInlineLoading={isInlineLoading}
              totalResults={totalResults}
              results={results}
              onSeeMore={onSeeMore}
              fetchNextPage={() => fetchNextPage()}
              resultViewType={resultViewType}
              hideFilter={hideFilter}
            >
              {typeFilter.options.length > 0 ? (
                <TabBar
                  filter={typeFilter}
                  activeFilter={activeTypeFilter}
                  setActiveFilter={(newActiveTypeFilter) => {
                    setActiveFilters([])
                    setActiveTypeFilter(newActiveTypeFilter)
                    applyFilters(filters, [], query, filtersToApply, newActiveTypeFilter, selectedSortOption)
                  }}
                >
                  {sortBy}
                </TabBar>
              ) : totalResults > 0 && hideResultsHeader === false ? (
                <ResultsHeader>
                  <TotalResultsText>
                    {totalResults} {totalResults === 1 ? <Translate id="result" /> : <Translate id="results" />}
                  </TotalResultsText>
                  <div style={{ flex: '1 1 100%' }}></div>

                  {sortBy}
                </ResultsHeader>
              ) : (
                <></>
              )}
            </Content>
          </div>
        </div>
      </div>
    </TranslationsContext.Provider>
  )
}

type ContentProps = {
  apiHost: string
  hasError: boolean
  isLoading: boolean
  isInlineLoading: boolean
  totalResults: number
  results: ResultGroupFromServer[]
  onSeeMore: (filterId: number) => void
  fetchNextPage: () => void
  resultViewType: string | undefined
  hideFilter: boolean
}

const getResultComponent = (resultViewType: string | undefined) => {
  switch (resultViewType) {
    case 'loading':
      return () => null // Show nothing while loading
    case 'urPlusProduct':
      return ImageGrid
    case 'urMedia':
      return MediaGrid
    case 'training':
      return TrainingGrid
    default:
      return ArticleList
  }
}

const Content: React.FC<ContentProps> = memo(
  ({
    apiHost,
    hasError,
    isLoading,
    totalResults,
    results,
    onSeeMore,
    fetchNextPage,
    isInlineLoading,
    children,
    resultViewType,
    hideFilter
  }) => {
    const ResultComponent = getResultComponent(resultViewType)

    return (
      <>
        {children}

        {hasError ? (
          <div>
            <h3 className="grid-text-center grid-gap5-above grid-gap5-below">
              <Translate id="errorText" />
            </h3>
          </div>
        ) : isLoading ? (
          <div>
            <h3 className="grid-text-center dots grid-gap5-above grid-gap5-below">
              <Translate id="loading" />
              <span>.</span>
              <span>.</span>
              <span>.</span>
              <p></p>
            </h3>
          </div>
        ) : totalResults === 0 ? (
          <div>
            <h3 className="grid-text-center grid-gap5-above grid-gap5-below">
              <Translate id="noResults" />
            </h3>
          </div>
        ) : (
          <>
            {results.map((group, i) => (
              <ResultComponent
                key={i}
                group={group}
                fetchNextPage={fetchNextPage}
                isInlineLoading={isInlineLoading}
                onSeeMore={onSeeMore}
                totalGroups={results.length}
                apiHost={apiHost}
                hideFilter={hideFilter}
              />
            ))}
          </>
        )}
      </>
    )
  }
)
